import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaMagic, FaUpload, FaMicrophone, FaArrowRight, FaBriefcase, FaSun, FaMoon } from 'react-icons/fa';

const SkillForgeLanding = () => {
  const navigate = useNavigate();
  const [prompt, setPrompt] = useState('');
  const [isTargeted, setIsTargeted] = useState(false);
  const [jobDescription, setJobDescription] = useState('');
  const [isDarkMode, setIsDarkMode] = useState(true);
  const [isListening, setIsListening] = useState(false);

  const maxPromptLength = 5000;
  const maxJobLength = 5000;

  const startVoiceInput = () => {
    if (!('webkitSpeechRecognition' in window) && !('SpeechRecognition' in window)) {
      alert('La reconnaissance vocale n\'est pas supportée. Utilisez Chrome ou Edge.');
      return;
    }

    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    const recognition = new SpeechRecognition();
    recognition.lang = 'fr-FR';
    recognition.continuous = false;
    recognition.interimResults = false;
    recognition.onstart = () => setIsListening(true);
    recognition.onresult = (event) => {
      const transcript = event.results[0][0].transcript;
      setPrompt(prev => (prev + ' ' + transcript).trim().slice(0, maxPromptLength));
      setIsListening(false);
    };
    recognition.onerror = () => setIsListening(false);
    recognition.onend = () => setIsListening(false);
    recognition.start();
  };

  const handleSubmit = () => {
    if (!prompt.trim()) {
      alert('Veuillez décrire votre parcours ou coller votre CV');
      return;
    }
    const promptData = { prompt: prompt.trim(), jobDescription: isTargeted ? jobDescription.trim() : null };
    localStorage.setItem('pendingPrompt', JSON.stringify(promptData));
    navigate('/dashboard');
  };

  const handleFileUpload = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (event) => {
        const text = event.target?.result;
        setPrompt(text?.toString().slice(0, maxPromptLength) || '');
      };
      reader.readAsText(file);
    }
  };

  const toggleTargeted = () => {
    setIsTargeted(!isTargeted);
  };

  return (
    <div className={`min-h-screen transition-colors duration-300 ${isDarkMode ? 'bg-[#1a1a1d] text-gray-200' : 'bg-white text-gray-900'}`}>
      {/* Navigation */}
      <nav className="fixed top-0 w-full z-50 backdrop-blur-sm border-b border-gray-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 sm:py-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <FaMagic className="w-5 h-5 sm:w-6 sm:h-6 text-[#00d9ff]" />
            <span className="text-lg sm:text-xl font-bold">
              SkillForge <span className="text-[#00d9ff]">Pro</span>
            </span>
          </div>
          
          <div className="flex items-center gap-3 sm:gap-6">
            <button className="hidden sm:block text-sm hover:text-[#00d9ff] transition-colors">Pricing</button>
            <button className="hidden sm:block text-sm hover:text-[#00d9ff] transition-colors">Blog</button>
            <button onClick={() => setIsDarkMode(!isDarkMode)} className="p-2 rounded-lg hover:bg-gray-800 transition-colors">
              {isDarkMode ? <FaSun className="w-4 h-4 sm:w-5 sm:h-5" /> : <FaMoon className="w-4 h-4 sm:w-5 sm:h-5" />}
            </button>
            <button onClick={() => navigate('/auth')} className="px-3 py-2 sm:px-4 rounded-lg bg-[#00d9ff] hover:bg-[#00c4ea] transition-colors text-sm font-medium text-black">
              Connexion
            </button>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <div className="pt-24 sm:pt-32 pb-20 px-4 sm:px-6">
        <div className="max-w-4xl mx-auto">
          {/* Title */}
          <h1 className="text-center text-gray-400 text-sm sm:text-base mb-6 sm:mb-8">
            Décrivez votre parcours professionnel, je génère votre CV parfait.
          </h1>

          {/* Job Description Field (Conditional) */}
          {isTargeted && (
            <div className="mb-4 animate-fade-in">
              <div className="relative">
                <textarea 
                  value={jobDescription} 
                  onChange={(e) => setJobDescription(e.target.value.slice(0, maxJobLength))} 
                  placeholder="Collez l'annonce du poste ciblé..."
                  className={`w-full p-4 rounded-2xl border ${isDarkMode ? 'bg-[#242428] border-gray-700 text-gray-200 placeholder-gray-500' : 'bg-gray-50 border-gray-300 text-gray-900 placeholder-gray-400'} focus:outline-none focus:ring-2 focus:ring-[#00d9ff] resize-none`}
                  rows={3}
                />
                <div className="absolute top-2 right-3 text-xs text-gray-500">{jobDescription.length}/{maxJobLength}</div>
              </div>
            </div>
          )}

          {/* Main Prompt Input */}
          <div className="relative">
            <textarea
              value={prompt}
              onChange={(e) => setPrompt(e.target.value.slice(0, maxPromptLength))}
              placeholder="Décrivez votre parcours, je vous génère un beau CV fonctionnel"
              className={`w-full p-4 sm:p-6 pr-32 sm:pr-36 rounded-2xl border ${isDarkMode ? 'bg-[#242428] border-gray-700 text-gray-200 placeholder-gray-500' : 'bg-gray-50 border-gray-300 text-gray-900 placeholder-gray-400'} focus:outline-none focus:ring-2 focus:ring-[#00d9ff] resize-none text-sm sm:text-base`}
              rows={window.innerWidth < 640 ? 4 : 6}
            />
            
            {/* Icon Bar Inside Textarea */}
            <div className="absolute bottom-3 sm:bottom-4 left-3 sm:left-4 right-3 sm:right-4 flex items-center justify-between">
              {/* Left Icons */}
              <div className="flex items-center gap-1 sm:gap-2">
                {/* Target Job Button */}
                <button
                  onClick={toggleTargeted}
                  className={`group flex items-center gap-1 sm:gap-2 px-2 sm:px-3 py-1.5 sm:py-2 rounded-lg transition-all ${
                    isTargeted 
                      ? 'bg-[#00d9ff] text-black' 
                      : isDarkMode ? 'hover:bg-gray-700 text-gray-400' : 'hover:bg-gray-200 text-gray-600'
                  }`}
                  title="Poste ciblé"
                >
                  <FaBriefcase className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                  <span className="hidden sm:inline text-xs font-medium">Poste ciblé</span>
                </button>
              </div>

              {/* Right Icons */}
              <div className="flex items-center gap-1 sm:gap-2">
                {/* Upload CV */}
                <label className={`p-1.5 sm:p-2 rounded-lg cursor-pointer transition-colors ${isDarkMode ? 'hover:bg-gray-700 text-gray-400' : 'hover:bg-gray-200 text-gray-600'}`} title="Uploader un CV">
                  <FaUpload className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                  <input type="file" accept=".txt" onChange={handleFileUpload} className="hidden" />
                </label>

                {/* Voice Input */}
                <button
                  onClick={startVoiceInput}
                  disabled={isListening}
                  className={`p-1.5 sm:p-2 rounded-lg transition-colors ${
                    isListening 
                      ? 'bg-red-500 text-white animate-pulse' 
                      : isDarkMode ? 'hover:bg-gray-700 text-gray-400' : 'hover:bg-gray-200 text-gray-600'
                  }`}
                  title="Dicter"
                >
                  <FaMicrophone className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                </button>

                {/* Submit Button */}
                <button
                  onClick={handleSubmit}
                  disabled={!prompt.trim()}
                  className={`p-2 sm:p-2.5 rounded-lg transition-all ${
                    prompt.trim()
                      ? 'bg-[#00d9ff] hover:bg-[#00c4ea] text-black'
                      : isDarkMode ? 'bg-gray-700 text-gray-600' : 'bg-gray-200 text-gray-400'
                  }`}
                  title="Envoyer"
                >
                  <FaArrowRight className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                </button>
              </div>
            </div>

            {/* Character Counter */}
            <div className="absolute top-2 right-3 text-xs text-gray-500">{prompt.length}/{maxPromptLength}</div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className="fixed bottom-0 w-full pb-6 sm:pb-8 pointer-events-none">
        <div className="flex items-center justify-center gap-4 sm:gap-6 text-xs sm:text-sm text-gray-500 pointer-events-auto">
          <button className="hover:text-[#00d9ff] transition-colors">
            Construire un profil
          </button>
          <button className="hover:text-[#00d9ff] transition-colors">
            Trouver des opportunités
          </button>
        </div>
      </div>
    </div>
  );
};

export default SkillForgeLanding;
